import React, { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { BoxArrowUpRight, Download } from "react-bootstrap-icons";
import { BaseWidget, WidgetIconButton } from "../common";
import { DateRangeSelect } from "../common/DateRangeSelect";
import { formatCurrency, getRangeDates } from "../../constants";
import "../../styles/CommissionWidgets.css";

const GROUPS = [
  { key: "location", label: "By Location" },
  { key: "agent", label: "By Agent" },
];

/**
 * Commission Reporting — date-ranged totals rolled up by location or agent.
 * Expanding a row reveals the other dimension.
 */
export function CommissionReportingWidget({
  data,
  range,
  onRangeChange,
  isLoading,
  error,
  onNavigate,
  onDownload,
}) {
  const [groupBy, setGroupBy] = useState("location");
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleRow = (name) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const handleGroupChange = (key) => {
    setGroupBy(key);
    setExpanded(new Set());
  };

  const { groups, grandTotal, maxTotal } = useMemo(() => {
    const records = data?.rows || [];
    const primary = groupBy === "location" ? "location" : "agent";
    const secondary = groupBy === "location" ? "agent" : "location";

    const map = {};
    records.forEach((r) => {
      const key = r[primary] || "Unassigned";
      if (!map[key]) map[key] = { name: key, total: 0, children: {} };
      map[key].total += r.total || 0;

      const childKey = r[secondary] || "Unassigned";
      if (!map[key].children[childKey]) {
        map[key].children[childKey] = {
          name: childKey,
          total: 0,
        };
      }
      map[key].children[childKey].total += r.total || 0;
    });

    const list = Object.values(map)
      .map((g) => ({
        ...g,
        children: Object.values(g.children).sort((a, b) => b.total - a.total),
      }))
      .sort((a, b) => b.total - a.total);

    return {
      groups: list,
      grandTotal: list.reduce((s, g) => s + g.total, 0),
      maxTotal: list.length ? list[0].total : 0,
    };
  }, [data, groupBy]);

  const resolvedDates = useMemo(() => getRangeDates(range), [range]);
  const rangeLabel = useMemo(() => {
    const { startDate, endDate } = resolvedDates || {};
    if (!startDate || !endDate) return "";
    const fmt = (v) => {
      const [y, m, d] = String(v).slice(0, 10).split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  }, [resolvedDates]);

  const topLabel = groups.length
    ? `${groups[0].name} (${formatCurrency(groups[0].total)})`
    : "";

  const handleNavigate = () => {
    const search = new URLSearchParams({
      commissionStatus: "Approved",
      signedStartDate: resolvedDates.startDate,
      signedEndDate: resolvedDates.endDate,
    }).toString();

    onNavigate(`/commissions-contracts?${search}`);
  };

  return (
    <BaseWidget
      className="cp-cwidget"
      title="Commissions"
      isLoading={isLoading}
      error={error}
      headerRight={
        <>
          <DateRangeSelect value={range} onChange={onRangeChange} />
          {onDownload && (
            <WidgetIconButton
              icon={<Download />}
              title="Download"
              onClick={onDownload}
            />
          )}
          <WidgetIconButton
            icon={<BoxArrowUpRight />}
            title="View all"
            onClick={handleNavigate}
          />
        </>
      }
    >
      {data && (
        <Row className="g-2 align-items-end justify-content-between cp-cwidget__head">
          <Col xs="auto">
            <span className="cp-cwidget__total">
              {formatCurrency(grandTotal)}
            </span>
            <span className="cp-cwidget__total-label">commissions</span>
          </Col>
          <Col xs="auto" className="text-end">
            {rangeLabel && <div className="cp-cwidget__meta">{rangeLabel}</div>}
            {topLabel && (
              <span className="cp-cwidget__meta">Top: {topLabel}</span>
            )}
          </Col>
        </Row>
      )}

      <div className="cp-cwidget__chips">
        {GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`cp-cwidget__chip${groupBy === g.key ? " is-active" : ""}`}
            onClick={() => handleGroupChange(g.key)}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div className="cp-cwidget__thead cp-report__grid">
        <span className="cp-cwidget__th">
          {groupBy === "location" ? "Location" : "Agent"}
        </span>
        <span className="cp-cwidget__th cp-cwidget__th--right">Total</span>
      </div>

      <div className="cp-cwidget__body">
        {groups.map((g) => {
          const isOpen = expanded.has(g.name);
          return (
            <div
              key={g.name}
              className="cp-report__row"
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggleRow(g.name)}
              onKeyDown={(e) => e.key === "Enter" && toggleRow(g.name)}
            >
              <div className="cp-cwidget__row cp-report__grid border-0 p-0">
                <div className="cp-report__name-wrap">
                  <span
                    className={`cp-report__caret${isOpen ? " is-open" : ""}`}
                  >
                    &#9656;
                  </span>
                  <span className="cp-report__name">{g.name}</span>
                </div>
                <div className="cp-report__total cp-cwidget__num cp-cwidget__cell--right">
                  {formatCurrency(g.total)}
                </div>
              </div>

              <div className="cp-report__bar">
                <div
                  className="cp-report__bar-fill"
                  style={{
                    width: `${maxTotal ? (g.total / maxTotal) * 100 : 0}%`,
                  }}
                />
              </div>

              {isOpen && (
                <div className="cp-report__children">
                  {g.children.map((c) => (
                    <div
                      key={c.name}
                      className="cp-cwidget__row cp-report__grid border-0 p-0"
                    >
                      <span className="cp-report__child-name">{c.name}</span>
                      <span className="cp-report__child-total cp-cwidget__num cp-cwidget__cell--right">
                        {formatCurrency(c.total)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </BaseWidget>
  );
}
